import React, { useId, useMemo } from 'react';
import { curveMonotoneX } from '@visx/curve';
import { AreaChart } from '../../charts/area-chart';
import { Area } from '../../charts/area';
import { Grid } from '../../charts/grid';
import { XAxis } from '../../charts/x-axis';
import { YAxis } from '../../charts/y-axis';
import { ChartTooltip } from '../../charts/tooltip';
import { ReferenceLine } from '../../charts/reference-line';
import { useChartStable, useYScale } from '../../charts/chart-context';
import { getFormatter } from '../formatters';
import {
  useIsDarkMode,
  useChartPaletteId,
  compactFormat,
  seriesColor,
  SYNTH_X_KEY,
  toBklitRows,
  seriesHasData,
  ChartSkeletonLayer,
  isNumeric,
  pointMarkers,
  referenceLabelColor,
} from './chartTheme';
import { isInkShell } from '../../../styles/chartColors';
import { ChartLegend } from './ChartLegend';
import { prefersReducedMotion } from '../AnimatedValue';
import '../ReportsNative.css';

/**
 * Split-mode stroke/fill gradients sized to the plot: userSpaceOnUse over
 * [0, innerWidth] with both stops at the divider fraction → hard color
 * switch exactly at the divider x. Reads innerWidth from chart context, so
 * it must be a chart child.
 */
function SplitGradientDefs({ strokeId, fillId, t, pastColor, futureColor }) {
  const { innerWidth } = useChartStable();
  // No <defs> wrapper: the shell classifies *Gradient* children into its own
  // <defs>, and paint servers are non-rendered wherever they land anyway.
  return (
    <>
      <linearGradient id={strokeId} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={Math.max(innerWidth, 1)} y2={0}>
        <stop offset={t} stopColor={pastColor} />
        <stop offset={t} stopColor={futureColor} />
      </linearGradient>
      <linearGradient id={fillId} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={Math.max(innerWidth, 1)} y2={0}>
        <stop offset={t} stopColor={pastColor} stopOpacity={0.16} />
        <stop offset={t} stopColor={futureColor} stopOpacity={0.12} />
      </linearGradient>
    </>
  );
}
SplitGradientDefs.displayName = 'SplitGradientDefs';

/** Per-point white-filled dots colored by past/future side of the divider. */
function SplitPointDots({ valueKey, splitIdx, allFuture, pastColor, futureColor }) {
  const { data, xScale, xAccessor } = useChartStable();
  const yScale = useYScale();
  return (
    <g pointerEvents="none">
      {data.map((d, i) => {
        const v = d[valueKey];
        if (!isNumeric(v)) return null;
        const cx = xScale(xAccessor(d)) ?? 0;
        const cy = yScale(v) ?? 0;
        const future = splitIdx >= 0 ? i >= splitIdx : allFuture;
        return (
          <circle
            key={`rnsplit-dot-${i}`}
            cx={cx}
            cy={cy}
            r={2.6}
            fill="var(--chart-background)"
            stroke={future ? futureColor : pastColor}
            strokeWidth={2}
          />
        );
      })}
    </g>
  );
}
SplitPointDots.displayName = 'SplitPointDots';

/**
 * Horizontal guide whose label lives in the right gutter. Flagged as a
 * post-overlay layer so the shell renders it OUTSIDE the plot's grow-clip —
 * a plain ReferenceLine child is clipped to innerWidth and loses the label.
 */
function GutterReferenceLine(props) {
  return <ReferenceLine {...props} />;
}
GutterReferenceLine.displayName = 'GutterReferenceLine';
GutterReferenceLine.__isPostOverlay = true;

/**
 * Actual-vs-OTB area chart with a dashed "Today" reference line (per
 * revenue-forecast.html "Room Revenue: Actual + On-the-Books"). Rendered
 * with the vendored bklit kit (src/components/charts) — categorical x
 * values get synthetic dates per the LODGEIC.md recipe.
 *
 * Props:
 *   data, xKey, series [{ key, label, color? }]
 *   referenceX       x value for the dashed divider (e.g. today's label)
 *   referenceLabel   divider label (default 'Today')
 *   stacked          stack series instead of overlay (default false).
 *                    bklit areas don't auto-stack — cumulative values are
 *                    precomputed into the rows and each band renders its
 *                    running total (tooltips still show raw values)
 *   split            ONE continuous line that switches color at the divider
 *                    instead of two separate series:
 *                    { key: 'value',        // merged past→future dataKey
 *                      x,                   // x value where the color flips
 *                                           //   (usually === referenceX)
 *                      past:   { label, color? },
 *                      future: { label, color? },
 *                      allFuture?,          // x not in data: true = whole
 *                                           //   line is future-colored
 *                      name? }              // tooltip series name
 *                    `series` is ignored when split is set
 *   yFormat (default 'currencyCompact'), currency, height (default 240),
 *   legend (default true), dots (default false), yDomain (unsupported by the
 *   kit — domain derives from data)
 *   xLabelKey        axis label field when xKey is a unique id, not display text
 *   yReferences      horizontal dashed guides labeled in the right gutter:
 *                    [{ key, value, label, color?, labelColor?, opacity? }].
 *                    Pass a wider `margin.right` to make room for the labels.
 */
export function StackedAreaChart({
  data = [],
  xKey,
  series = [],
  referenceX,
  referenceLabel = 'Today',
  stacked = false,
  split,
  yFormat = 'currencyCompact',
  currency,
  height = 240,
  legend = true,
  // 'peak' (default) labels only the top of the scale — the high-water
  // mark. 'full' restores the tick ladder for charts read for values
  // rather than shape.
  yAxis = 'peak',
  dots = false,
  yDomain, // eslint-disable-line no-unused-vars -- kept for API compatibility; kit derives domain from data
  margin,
  loading = false,
  // Extra tooltip-only rows read from the source point: [{ key, label, color }].
  // Skipped per-point when the value is not numeric. Lets a chart surface
  // series that aren't drawn (e.g. Projected alongside the Actual/OTB line).
  tooltipRows = [],
  yReferences = [],
  // Optional key for the axis label when `xKey` is a unique id (e.g.
  // "2026-01") rather than display text (e.g. "Jan").
  xLabelKey,
}) {
  const dark = useIsDarkMode();
  useChartPaletteId(); // repaint on a palette switch (series colours read the palette live)
  const gradientId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const tickFmt = getFormatter(compactFormat(yFormat), currency ? { currency } : undefined);
  const fmt = getFormatter(yFormat, currency ? { currency } : undefined);
  const splitKey = split ? split.key || 'value' : null;

  const rows = useMemo(() => {
    const keys = split ? [splitKey] : series.map((s) => s.key);
    const built = toBklitRows(data, keys);
    if (!split && stacked) {
      // bklit areas don't stack — precompute running totals per row; band i
      // renders `__rnStk{i}` (sum of series 0..i) back-to-front.
      for (const r of built) {
        let cum = 0;
        series.forEach((s, i) => {
          if (isNumeric(r[s.key])) cum += r[s.key];
          r[`__rnStk${i}`] = cum;
        });
      }
    }
    return built;
  }, [data, series, split, splitKey, stacked]);
  const xLabels = useMemo(() => rows.map((r) => String(r.__rnSrc?.[xLabelKey || xKey] ?? '')), [rows, xKey, xLabelKey]);

  const refIdx = useMemo(() => {
    if (referenceX === undefined || referenceX === null) return -1;
    return rows.findIndex((r) => r.__rnSrc?.[xKey] === referenceX);
  }, [rows, referenceX, xKey]);

  // Markers only on sparse series — dense daily data reads as noise.
  const sparse = rows.length <= 14;

  const base = margin || { top: 18, right: 16, bottom: 0, left: 0 };
  const kitMargin = {
    top: base.top,
    right: base.right,
    bottom: base.bottom + (isInkShell() ? 28 : 34),
    left: base.left + (isInkShell() ? 46 : 58),
  };

  // See TrendAreaChart: the chart stays mounted and only the skeleton layer
  // swaps out when the payload lands.
  const skeleton = loading && rows.length === 0;

  const shellProps = {
    className: 'rn-bklit',
    xDataKey: SYNTH_X_KEY,
    xLabels,
    margin: kitMargin,
    aspectRatio: null,
    style: { height: '100%' },
    animationDuration: prefersReducedMotion() ? 0 : undefined,
    // Loading is a LAYER (ChartSkeletonLayer below), not the kit's `status`
    // choreography — see ColumnTrendChart/LODGEIC.md for why: with real rows
    // empty during loading, `status="loading"` makes the shell synthesize
    // its OWN internal skeleton keyed `{date, value}` instead of our
    // SYNTH_X_KEY rows, so xAccessor reads undefined and the axis's
    // `new Date(undefined)` throws formatting it.
    status: 'ready',
  };

  /* ---------- split mode: a single line, color-switched at split.x ---------- */
  if (split) {
    const pastColor = split.past?.color || seriesColor(split.past || {}, 0, dark);
    const futureColor = split.future?.color || seriesColor(split.future || {}, 1, dark);
    const splitIdx = split.x !== undefined && split.x !== null ? rows.findIndex((r) => r.__rnSrc?.[xKey] === split.x) : -1;
    // Both gradient stops share the divider fraction → hard color switch.
    const t = splitIdx >= 0 && rows.length > 1 ? splitIdx / (rows.length - 1) : split.allFuture ? 0 : 1;
    const dashFromIndex = splitIdx >= 0 ? splitIdx : split.allFuture ? 0 : undefined;
    const legendSeries = [
      { key: 'past', label: split.past?.label || 'Actual', color: pastColor },
      { key: 'future', label: split.future?.label || 'Projected', color: futureColor },
    ];
    const strokeId = `rnSplitStroke-${gradientId}`;
    const fillId = `rnSplitFill-${gradientId}`;
    const sideColor = (i) => (splitIdx >= 0 ? (i >= splitIdx ? futureColor : pastColor) : split.allFuture ? futureColor : pastColor);
    return (
      <div>
        {legend && <ChartLegend series={legendSeries} />}
        <div className="rn-chart-body" style={{ height }}>
          <AreaChart data={rows} {...shellProps}>
            <SplitGradientDefs strokeId={strokeId} fillId={fillId} t={t} pastColor={pastColor} futureColor={futureColor} />
            <Grid horizontal numTicksRows={4} />
            <XAxis />
            {!skeleton && <YAxis peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => tickFmt(v)} />}
            {!skeleton && (
              <ChartTooltip
                title={null}
                rows={(point) => {
                  const i = point.__rnIdx ?? 0;
                  const future = splitIdx >= 0 ? i >= splitIdx : Boolean(split.allFuture);
                  // Label the merged line by which side of the divider the point
                  // sits on (Past Room Revenue vs OTB Revenue), not a generic name.
                  const sideLabel = future
                    ? (split.future?.label || split.name || 'Value')
                    : (split.past?.label || split.name || 'Value');
                  const extras = tooltipRows
                    .filter((r) => isNumeric(point.__rnSrc?.[r.key]))
                    .map((r) => ({ color: r.color, label: r.label, value: fmt(point.__rnSrc?.[r.key]) }));
                  return [
                    { color: sideColor(i), label: sideLabel, value: fmt(point.__rnSrc?.[splitKey]) },
                    ...extras,
                  ];
                }}
              />
            )}
            {refIdx >= 0 && (
              <ReferenceLine
                x={rows[refIdx][SYNTH_X_KEY]}
                stroke={pastColor}
                strokeWidth={1}
                strokeDasharray="3 3"
                opacity={0.55}
                label={referenceLabel}
                labelFill={referenceLabelColor(dark)}
                labelFontSize={9}
                labelFontFamily="var(--font-sans)"
                labelDy={14}
              />
            )}
            {!skeleton && yReferences.filter((r) => isNumeric(r.value)).map((r) => (
              <GutterReferenceLine
                key={`rn-yref-${r.key ?? r.label}`}
                y={r.value}
                stroke={r.color || 'var(--lodge-onyx-300, #b8b8c4)'}
                strokeDasharray="3 4"
                opacity={r.opacity ?? 0.8}
                label={r.label}
                labelFill={r.labelColor || r.color || 'var(--lodge-onyx-400, #8a8a96)'}
                labelFontSize={10}
                labelDx={base.right - 2}
                labelDy={8}
              />
            ))}
            {seriesHasData(rows, splitKey) && (
              <Area
                dataKey={splitKey}
                curve={curveMonotoneX}
                stroke={`url(#${strokeId})`}
                strokeWidth={2.5}
                fill={`url(#${fillId})`}
                dashFromIndex={dashFromIndex}
              />
            )}
            {dots && sparse && (
              <SplitPointDots
                valueKey={splitKey}
                splitIdx={splitIdx}
                allFuture={Boolean(split.allFuture)}
                pastColor={pastColor}
                futureColor={futureColor}
              />
            )}
          </AreaChart>
        </div>
      </div>
    );
  }

  return (
    <div>
      {legend && <ChartLegend series={series} />}
      <div className="rn-chart-body" style={{ height }}>
        <AreaChart data={rows} {...shellProps}>
          <Grid horizontal numTicksRows={4} />
          <XAxis />
          {!skeleton && <YAxis peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => tickFmt(v)} />}
          {!skeleton && (
            <ChartTooltip
              title={null}
              rows={(point) => [
                ...series.map((s, i) => ({
                  color: seriesColor(s, i, dark),
                  label: s.label || s.key,
                  value: fmt(point.__rnSrc?.[s.key]),
                })),
                ...tooltipRows
                  .filter((r) => isNumeric(point.__rnSrc?.[r.key]))
                  .map((r) => ({ color: r.color, label: r.label, value: fmt(point.__rnSrc?.[r.key]) })),
              ]}
            />
          )}
          {refIdx >= 0 && (
            <ReferenceLine
              x={rows[refIdx][SYNTH_X_KEY]}
              stroke={seriesColor(series[0] || {}, 0, dark)}
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.55}
              label={referenceLabel}
              labelFill={referenceLabelColor(dark)}
              labelFontSize={9}
              labelFontFamily="var(--font-sans)"
              labelDy={14}
            />
          )}
          {skeleton && (
            <ChartSkeletonLayer color={seriesColor(series[0] || {}, 0, dark)} curve={curveMonotoneX} withArea />
          )}
          {(stacked ? [...series.keys()].reverse() : [...series.keys()]).map((i) => {
            const s = series[i];
            if (!seriesHasData(rows, s.key)) return null;
            const color = seriesColor(s, i, dark);
            if (stacked) {
              // Paint-over stacking: draw the LARGEST cumulative band first
              // and band 0 last, each with an OPAQUE pre-blended tint —
              // translucent fills composited over each other turn to mud.
              return (
                <Area
                  key={s.key}
                  dataKey={`__rnStk${i}`}
                  curve={curveMonotoneX}
                  stroke={color}
                  strokeWidth={1.5}
                  fill={`color-mix(in srgb, ${color} 22%, var(--chart-background))`}
                  fillOpacity={1}
                  gradientToOpacity={1}
                  showMarkers={false}
                />
              );
            }
            return (
              <Area
                key={s.key}
                dataKey={s.key}
                curve={curveMonotoneX}
                stroke={color}
                strokeWidth={2.5}
                fill={color}
                fillOpacity={i === 0 ? 0.22 : 0.18}
                gradientToOpacity={0}
                showMarkers={dots && sparse}
                markers={pointMarkers(color, 2.6)}
              />
            );
          })}
        </AreaChart>
      </div>
    </div>
  );
}

export default StackedAreaChart;
